import { endpoints } from '../api';
import type { Voice } from '../api/types';
import { isServerMode } from '../platform/server-mode';

/* Избранное: сердце в карточках голоса и персонажа, подборка «Избранное» в выборе голоса (docs/web/voice-favorites/README.md, решение 03.11.2026).
   Сервер — GET /app/favorites и PUT/DELETE /app/favorites/{voices|avatars}/{id}; без сервера (прототип) — память браузера.
   Нажатие применяется сразу; отказ сервера возвращает прежнее состояние, а ошибка уходит вызывающему (он скажет о ней).
   Любое изменение списка — событие steos:fav на document: выбор голоса и карточки перерисовывают сердце и подборку */

export type FavKind = 'voice' | 'avatar';
type Lists = Record<FavKind, number[]>;

const KEY = 'steos:bot:fav';
let lists: Lists = { voice: [], avatar: [] };
/** Избранное доступно: без сервера — сразу, с сервером — после первой удачной загрузки (старый backend без /app/favorites — сердец нет) */
let ready = false;
/** Номер последнего нажатия по каждой паре вид·id: ответ на устаревшее нажатие состояние не откатывает */
const turns = new Map<string, number>();

const ids = (value: unknown) => (Array.isArray(value) ? value.filter((id): id is number => Number.isSafeInteger(id) && id > 0) : []);

function readLocal(): Lists {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || 'null') as Partial<Lists> | null;
    return { voice: ids(raw?.voice), avatar: ids(raw?.avatar) };
  } catch {
    return { voice: [], avatar: [] };
  }
}

function writeLocal() {
  try { localStorage.setItem(KEY, JSON.stringify(lists)); } catch { /* память браузера недоступна — избранное живёт до перезагрузки */ }
}

const emit = () => document.dispatchEvent(new CustomEvent('steos:fav'));

export const favorites = {
  /** Сервер: избранное пользователя. Голоса возвращаются карточками — каркас кладёт их в каталог, чтобы подборка рисовалась без догрузки */
  async load(signal?: AbortSignal): Promise<Voice[]> {
    const page = await endpoints.favorites(signal);
    lists = { voice: page.voices.map(voice => voice.id), avatar: ids(page.avatar_ids) };
    ready = true;
    emit();
    return page.voices;
  },
  /** Можно ли показывать сердца и подборку «Избранное» */
  available() {
    return ready;
  },
  has(kind: FavKind, id: unknown) {
    return lists[kind].includes(Number(id));
  },
  /** id в порядке добавления, новые первыми */
  list(kind: FavKind) {
    return [...lists[kind]];
  },
  count() {
    return lists.voice.length + lists.avatar.length;
  },
  /** Добавить (on) или убрать; без on — переключить. Возвращает новое состояние; отказ сервера — исключение, состояние прежнее */
  async toggle(kind: FavKind, rawId: unknown, on?: boolean): Promise<boolean> {
    const id = Number(rawId);
    if (!Number.isSafeInteger(id) || id <= 0) throw new Error('favorite_id');
    const next = on ?? !favorites.has(kind, id);
    const key = kind + ':' + id;
    const turn = (turns.get(key) ?? 0) + 1;
    turns.set(key, turn);
    const before = lists[kind];
    lists = { ...lists, [kind]: next ? [id, ...before.filter(x => x !== id)] : before.filter(x => x !== id) };
    emit();
    if (!isServerMode()) {
      writeLocal();
      return next;
    }
    try {
      await endpoints.setFavorite(kind === 'voice' ? 'voices' : 'avatars', id, next);
    } catch (error) {
      if (turns.get(key) === turn) {
        /* на прежнее место: остальные нажатия за это время не теряются */
        const now = lists[kind].filter(x => x !== id);
        const at = before.indexOf(id);
        if (at >= 0) now.splice(Math.min(at, now.length), 0, id);
        lists = { ...lists, [kind]: now };
        emit();
      }
      throw error;
    }
    return next;
  },
};

export type Favorites = typeof favorites;

/** Мост для движка каркаса (window.BotFav). Без сервера список берётся из памяти браузера; с сервером его загружает installServerState */
export function installFavoritesBridge() {
  ready = !isServerMode();
  if (ready) lists = readLocal();
  (window as unknown as { BotFav?: Favorites }).BotFav = favorites;
}
